<template>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="visible"
        class="alert-dialog-backdrop"
        @click="closeOnBackdrop && close()"
      >
        <div
          class="alert-dialog"
          role="alertdialog"
          :aria-labelledby="`alert-title-${id}`"
          :aria-describedby="`alert-message-${id}`"
          @click.stop
        >
          <div class="alert-dialog-icon" v-if="showIcon">
            <!-- Error icon -->
            <svg v-if="type === 'error'" xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>

            <!-- Warning icon -->
            <svg v-else-if="type === 'warning'" xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>

            <!-- Network error icon -->
            <svg v-else-if="type === 'network'" xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clip-rule="evenodd" />
            </svg>

            <!-- Info icon -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>

          <div class="alert-dialog-content">
            <h2 :id="`alert-title-${id}`" class="alert-dialog-title">{{ title }}</h2>
            <p :id="`alert-message-${id}`" class="alert-dialog-message">{{ message }}</p>
          </div>

          <div class="alert-dialog-actions">
            <button
              v-if="showRetryButton"
              class="alert-dialog-retry-button"
              @click="onRetry"
            >
              {{ retryButtonText }}
            </button>

            <button
              class="alert-dialog-close-button"
              @click="close"
            >
              {{ closeButtonText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Generate a unique ID for this alert dialog
const id = Math.random().toString(36).substring(2, 9)

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'error',
    validator: (value: string) => ['error', 'warning', 'info', 'network'].includes(value)
  },
  showIcon: {
    type: Boolean,
    default: true
  },
  showRetryButton: {
    type: Boolean,
    default: false
  },
  retryButtonText: {
    type: String,
    default: ''
  },
  closeButtonText: {
    type: String,
    default: ''
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  autoClose: {
    type: Boolean,
    default: false
  },
  duration: {
    type: Number,
    default: 5000 // 5 seconds
  }
})

const emit = defineEmits(['close', 'retry'])

const visible = ref(false)
let autoCloseTimer: number | null = null

// Show the alert dialog
onMounted(() => {
  // Small delay to ensure proper animation
  setTimeout(() => {
    visible.value = true

    // Set up auto-close if enabled
    if (props.autoClose && props.duration > 0) {
      autoCloseTimer = window.setTimeout(() => {
        close()
      }, props.duration)
    }
  }, 50)
})

// Clean up on unmount
onBeforeUnmount(() => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
})

// Close the alert dialog
function close() {
  // Clear auto-close timer if it exists
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }

  visible.value = false
  // Small delay to allow animation to complete
  setTimeout(() => {
    emit('close')
  }, 300)
}

// Handle retry button click
function onRetry() {
  // Clear auto-close timer if it exists
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }

  emit('retry')
  close()
}
</script>

<style scoped>
.alert-dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.alert-dialog {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-width: 28rem;
  width: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.alert-dialog-icon {
  margin-bottom: 1rem;
}

.alert-dialog-content {
  margin-bottom: 1.5rem;
  width: 100%;
}

.alert-dialog-title {
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.alert-dialog-message {
  font-size: 1.3rem;
  color: #4b5563;
  line-height: 1.5;
}

.alert-dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  width: 100%;
}

.alert-dialog-retry-button {
  padding: 0.5rem 1rem;
  background-color: #3b82f6;
  color: white;
  border-radius: 0.375rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1.3rem;
}

.alert-dialog-retry-button:hover {
  background-color: #2563eb;
}

.alert-dialog-close-button {
  padding: 0.5rem 1rem;
  background-color: #f3f4f6;
  color: #1f2937;
  border-radius: 0.375rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1.3rem;
}

.alert-dialog-close-button:hover {
  background-color: #e5e7eb;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .alert-dialog {
    background-color: #1f2937;
  }

  .alert-dialog-title {
    color: #f9fafb;
  }

  .alert-dialog-message {
    color: #d1d5db;
  }

  .alert-dialog-close-button {
    background-color: #374151;
    color: #f9fafb;
  }

  .alert-dialog-close-button:hover {
    background-color: #4b5563;
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
